import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Copy, Check, FileCode } from 'lucide-react';
import { MermaidDiagram } from './MermaidDiagram';

interface MarkdownRendererProps {
  content: string;
  theme?: 'dark' | 'light';
  className?: string;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  theme = 'dark',
  className = ''
}) => {
  const isDark = theme === 'dark';
  const [copiedSnippetIdx, setCopiedSnippetIdx] = useState<string | null>(null);

  const handleCopyCode = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedSnippetIdx(id);
    setTimeout(() => setCopiedSnippetIdx(null), 2000);
  };

  return (
    <div
      className={`markdown-content w-full overflow-hidden leading-relaxed select-text ${
        isDark ? 'text-zinc-200' : 'text-zinc-900'
      } ${className}`}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ node, ...props }) => (
            <h1
              className={`text-base sm:text-lg font-bold mt-4 mb-2 pb-1.5 border-b flex items-center gap-2 ${
                isDark
                  ? 'text-white border-zinc-800'
                  : 'text-zinc-950 border-zinc-200'
              }`}
              {...props}
            />
          ),
          h2: ({ node, ...props }) => (
            <h2
              className={`text-sm sm:text-base font-bold mt-3.5 mb-2 pb-1 flex items-center gap-2 ${
                isDark ? 'text-violet-300' : 'text-violet-900'
              }`}
              {...props}
            />
          ),
          h3: ({ node, ...props }) => (
            <h3
              className={`text-xs sm:text-sm font-semibold mt-3 mb-1.5 ${
                isDark ? 'text-zinc-100' : 'text-zinc-800'
              }`}
              {...props}
            />
          ),
          h4: ({ node, ...props }) => (
            <h4
              className={`text-xs font-semibold mt-2.5 mb-1 ${
                isDark ? 'text-zinc-300' : 'text-zinc-700'
              }`}
              {...props}
            />
          ),
          p: ({ node, ...props }) => (
            <p
              className={`mb-2.5 last:mb-0 leading-relaxed text-xs sm:text-[13px] ${
                isDark ? 'text-zinc-200' : 'text-zinc-800'
              }`}
              {...props}
            />
          ),
          ul: ({ node, ...props }) => (
            <ul
              className="list-disc pl-5 mb-3 space-y-1 text-xs sm:text-[13px]"
              {...props}
            />
          ),
          ol: ({ node, ...props }) => (
            <ol
              className="list-decimal pl-5 mb-3 space-y-1 text-xs sm:text-[13px]"
              {...props}
            />
          ),
          li: ({ node, ...props }) => (
            <li className="leading-relaxed marker:text-violet-400" {...props} />
          ),
          strong: ({ node, ...props }) => (
            <strong
              className={`font-semibold ${
                isDark ? 'text-white' : 'text-zinc-950'
              }`}
              {...props}
            />
          ),
          em: ({ node, ...props }) => (
            <em
              className={`italic ${
                isDark ? 'text-zinc-300' : 'text-zinc-700'
              }`}
              {...props}
            />
          ),
          blockquote: ({ node, ...props }) => (
            <blockquote
              className={`border-l-4 my-3 px-3.5 py-2 rounded-r-xl italic text-xs leading-relaxed ${
                isDark
                  ? 'border-violet-500 bg-violet-950/30 text-zinc-300'
                  : 'border-blue-500 bg-blue-50/80 text-zinc-700'
              }`}
              {...props}
            />
          ),
          table: ({ node, ...props }) => (
            <div
              className={`overflow-x-auto my-3 rounded-xl border shadow-sm ${
                isDark
                  ? 'border-zinc-800 bg-zinc-950/70'
                  : 'border-zinc-200 bg-white'
              }`}
            >
              <table
                className="w-full text-left border-collapse text-xs"
                {...props}
              />
            </div>
          ),
          thead: ({ node, ...props }) => (
            <thead
              className={`border-b ${
                isDark
                  ? 'bg-zinc-800/90 text-zinc-100 border-zinc-700'
                  : 'bg-zinc-100 text-zinc-900 border-zinc-200'
              }`}
              {...props}
            />
          ),
          th: ({ node, ...props }) => (
            <th
              className="px-3.5 py-2.5 font-semibold tracking-wide text-xs whitespace-nowrap"
              {...props}
            />
          ),
          tbody: ({ node, ...props }) => (
            <tbody
              className={`divide-y ${
                isDark ? 'divide-zinc-800/80' : 'divide-zinc-200'
              }`}
              {...props}
            />
          ),
          tr: ({ node, ...props }) => (
            <tr
              className={`transition-colors ${
                isDark ? 'hover:bg-zinc-800/40' : 'hover:bg-zinc-50'
              }`}
              {...props}
            />
          ),
          td: ({ node, ...props }) => (
            <td className="px-3.5 py-2 text-xs leading-normal" {...props} />
          ),
          hr: ({ node, ...props }) => (
            <hr
              className={`my-3.5 border-t ${
                isDark ? 'border-zinc-800' : 'border-zinc-200'
              }`}
              {...props}
            />
          ),
          a: ({ node, href, children, ...props }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-violet-400 hover:text-violet-300 underline underline-offset-2 transition-colors font-medium"
              {...props}
            >
              {children}
            </a>
          ),
          code: ({ node, className: codeClassName, children, ...props }) => {
            const match = /language-(\w+)/.exec(codeClassName || '');
            const isInline = !codeClassName && typeof children === 'string' && !children.includes('\n');
            const codeString = String(children).replace(/\n$/, '');
            const snippetId = `code_${Math.random().toString(36).substring(2, 9)}`;

            if (match && match[1].toLowerCase() === 'mermaid') {
              return <MermaidDiagram chart={codeString} isDark={isDark} />;
            }

            if (isInline) {
              return (
                <code
                  className={`px-1.5 py-0.5 rounded font-mono text-[11px] border ${
                    isDark
                      ? 'bg-zinc-800/90 text-violet-300 border-zinc-700/60'
                      : 'bg-zinc-100 text-violet-800 border-zinc-300'
                  }`}
                  {...props}
                >
                  {children}
                </code>
              );
            }

            return (
              <div
                className={`my-3 rounded-xl border overflow-hidden shadow-md ${
                  isDark
                    ? 'bg-zinc-950 border-zinc-800'
                    : 'bg-zinc-900 border-zinc-700 text-zinc-100'
                }`}
              >
                <div
                  className={`flex items-center justify-between px-3.5 py-1.5 text-[11px] font-mono border-b ${
                    isDark
                      ? 'bg-zinc-900/90 text-zinc-400 border-zinc-800'
                      : 'bg-zinc-800 text-zinc-300 border-zinc-700'
                  }`}
                >
                  <span className="flex items-center gap-1.5 font-bold uppercase tracking-wider text-violet-400">
                    <FileCode className="w-3.5 h-3.5" />
                    {match ? match[1] : 'Код / Дані'}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleCopyCode(codeString, snippetId)}
                    className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors cursor-pointer"
                    title="Скопіювати вміст блоку"
                  >
                    {copiedSnippetIdx === snippetId ? (
                      <>
                        <Check className="w-3 h-3 text-emerald-400" />
                        <span className="text-emerald-400 font-sans">Скопійовано</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        <span className="font-sans">Копіювати</span>
                      </>
                    )}
                  </button>
                </div>
                <pre className="p-3.5 text-xs font-mono overflow-x-auto leading-relaxed text-zinc-200">
                  <code className={codeClassName} {...props}>
                    {children}
                  </code>
                </pre>
              </div>
            );
          }
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
